import { message } from 'antd';
import config from 'config';
import { useEffect, useMemo, useRef, useState } from 'react';

/**
 * Configuration options for the `useRegionalFilterInit` hook.
 *
 * @template TFilterInput - The shape of the filter object used in the application.
 */
type UseRegionalFilterInitOptions<TFilterInput> = {
  /**
   * Interface to interact with the external filter state management.
   */
  filterHook: {
    /**
     * Updates a specific filter option.
     * @param option - Object containing the field name and the new value.
     */
    setFilterOption: (option: {
      fieldName: keyof TFilterInput;
      value: unknown;
    }) => void;

    /**
     * Retrieves the current state of the filter object.
     */
    getFilterObject: () => TFilterInput;
  };

  /**
   * The specific key within `TFilterInput` that this hook should manage/populate
   * (e.g., 'locationNames', 'storeIds').
   */
  filterFieldName: keyof TFilterInput;

  /**
   * Flag indicating if the user has permission to view data from all regions.
   * If `true`, auto-initialization of regional locations is skipped.
   */
  canViewAllRegions: boolean;

  /**
   * Whether the user has any location restrictions (assigned locations).
   * If `false`, no filter is applied — user sees everything.
   */
  hasLocationRestrictions: boolean;

  /**
   * Location names from user's assigned locations.
   * These are used to populate the filter.
   */
  assignedLocationNames: string[];

  /**
   * Master switch to enable or disable this hook's logic.
   * Defaults to `config.DEFAULTS.REGION_FILTRATION_ENABLED`.
   */
  isEnabled?: boolean;

  /**
   * Optional message to display (via toast/notification) when the filter is
   * successfully auto-initialized.
   */
  successMessage?: string;
};

/**
 * Return values from the `useRegionalFilterInit` hook.
 */
type UseRegionalFilterInitResult = {
  /**
   * Indicates if the hook is currently fetching location data.
   */
  isLoading: boolean;

  /**
   * Boolean indicating if the regional filter is currently active.
   * Returns `true` if the current filter value contains all default regional locations.
   */
  isRegionalFilterApplied: boolean;

  /**
   * Array of location names derived from `regionLocations`.
   * These are the values used to populate the filter.
   */
  defaultLocationNames: string[];
};

/**
 * A custom hook that automatically initializes a filter field with the user's assigned locations.
 *
 * This is primarily used for users who have restricted access to specific locations
 * (when `!canViewAllRegions` and `hasLocationRestrictions` are true).
 *
 * The hook performs the following steps:
 * 1. Checks if regional filtration is required based on user permissions and configuration.
 * 2. Verifies if the target filter field (`filterFieldName`) is currently empty.
 * 3. If empty, it populates the filter with the provided `assignedLocationNames`.
 * 4. Optionally displays a one-time success message.
 *
 * Note: This hook does not fetch data; it relies on `assignedLocationNames` being passed to it.
 *
 * @template TFilterInput - The generic type representing the structure of the filter object.
 * @param {UseRegionalFilterInitOptions<TFilterInput>} options - Configuration options.
 * @returns {UseRegionalFilterInitResult} State of the regional filter initialization.
 *
 * @example
 * const { isLoading, isRegionalFilterApplied } = useRegionalFilterInit({
 *   filterHook: { setFilterOption, getFilterObject },
 *   filterFieldName: 'locations',
 *   assignedLocationNames: user.assignedLocations, // ['New York', 'Boston']
 *   canViewAllRegions: user.isAdmin,
 *   hasLocationRestrictions: true,
 *   successMessage: 'Regional filter applied automatically'
 * });
 */
export const useRegionalFilterInit = <
  TFilterInput extends Record<string, unknown>
>({
  filterHook,
  filterFieldName,
  canViewAllRegions,
  hasLocationRestrictions,
  assignedLocationNames,
  isEnabled = config.DEFAULTS.REGION_FILTRATION_ENABLED,
  successMessage,
}: UseRegionalFilterInitOptions<TFilterInput>): UseRegionalFilterInitResult => {
  const [isInitialized, setIsInitialized] = useState(false);
  const hasShownMessage = useRef(false);

  // Determine if regional filter should be applied
  const needsRegionalFilter =
    isEnabled && !canViewAllRegions && hasLocationRestrictions;

  // One-time filter initialization
  useEffect(() => {
    if (isInitialized) return;

    if (!needsRegionalFilter) {
      setIsInitialized(true);
      return;
    }

    if (assignedLocationNames.length === 0) {
      setIsInitialized(true);
      return;
    }

    const currentFilter = filterHook.getFilterObject();
    const currentValue = currentFilter[filterFieldName];

    // Initialize only if the filter is currently empty
    const isFilterEmpty =
      !currentValue ||
      (Array.isArray(currentValue) && currentValue.length === 0);

    if (isFilterEmpty) {
      filterHook.setFilterOption({
        fieldName: filterFieldName,
        value: assignedLocationNames,
      });

      // Show success message once
      if (successMessage && !hasShownMessage.current) {
        message.info(successMessage);
        hasShownMessage.current = true;
      }
    }

    setIsInitialized(true);
  }, [
    needsRegionalFilter,
    isInitialized,
    assignedLocationNames,
    filterHook,
    filterFieldName,
    successMessage,
  ]);

  const isLoading = !isInitialized;

  // Determine if the regional filter is currently applied
  const isRegionalFilterApplied = useMemo(() => {
    if (!needsRegionalFilter) return false;
    if (assignedLocationNames.length === 0) return false;

    const currentFilter = filterHook.getFilterObject();
    const currentLocations = currentFilter[filterFieldName] as
      | string[]
      | undefined;

    if (!currentLocations || currentLocations.length === 0) {
      return false;
    }

    // Check if the current filter matches the regional locations
    return assignedLocationNames.every((name) =>
      currentLocations.includes(name)
    );
  }, [filterHook, filterFieldName, assignedLocationNames, needsRegionalFilter]);

  return {
    isLoading,
    isRegionalFilterApplied,
    defaultLocationNames: assignedLocationNames,
  };
};
